import { useMemo } from 'react';
import { FloatingPanel } from '@mews/b2b-ui';
import { LocalizedText, useDialog } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { usePlatformPrivilege } from '@mews-commander/core';
import { MfaConfiguration } from '@mews-commander/data-access';
import { OptimusProvider, UnsavedChangesDialog } from '@mews-commander/ui';
import { Button, Stack, useValueAsRef } from '@optimus-web/core';
import { useMfaSetupForm } from './hooks/useMfaSetupForm';
import { useSaveMfaSetup } from './hooks/useSaveMfaSetup';
import { ToggleMfa } from './ToggleMfa';
import { Exemptions } from './Exemptions';

interface Props {
    active: boolean;
    onClose: () => void;
    data: MfaConfiguration;
}

export const ExceptionsDialog = ({ onClose, active, data }: Props) => {
    const { onSave, isSaving } = useSaveMfaSetup({ onSuccess: onClose });
    const [{ active: confirmationActive, onClose: onConfirmationClose }, toggleConfirmationDialog] = useDialog();

    const exemptedEnterpriseProfileIds = useMemo(
        () => data?.exemptEnterpriseProfiles?.map((profile) => profile.id) || [],
        [data]
    );

    const { submit, renderFormContent, hasDirtyFields, reset } = useMfaSetupForm({
        exemptedEnterpriseProfileIds,
        enforceAuthenticatorSetup: !!data.enforceAuthenticatorSetup,
        onSubmit: onSave,
    });

    const isFormDirty = useValueAsRef(hasDirtyFields);

    const handleAttemptClose = () => {
        if (isFormDirty.current) {
            toggleConfirmationDialog();
        } else {
            onClose();
        }
    };

    const canAccessAllEnterprises = usePlatformPrivilege('accessAllEnterprises');
    const isPlatformAdmin = usePlatformPrivilege('admin');

    const isPrivilegedPlatformUser = canAccessAllEnterprises || isPlatformAdmin;

    return (
        <>
            <OptimusProvider density="spacious">
                <FloatingPanel
                    title={<LocalizedText textKey={TextKey.TwoFactorAuthenticationExceptionsTitle} />}
                    onClose={handleAttemptClose}
                    active={active}
                    footer={{
                        primary: (
                            <Button
                                size="medium"
                                variant="primary"
                                onClick={submit}
                                disabled={isSaving || !hasDirtyFields}
                                loading={isSaving}
                                type="submit"
                            >
                                <LocalizedText textKey={TextKey.Save} />
                            </Button>
                        ),
                        secondary: (
                            <Button size="medium" onClick={handleAttemptClose} type="button">
                                <LocalizedText textKey={TextKey.GoBack} />
                            </Button>
                        ),
                    }}
                    size="small"
                >
                    {renderFormContent(
                        <Stack vertical spacing="200">
                            <Exemptions data={data} />
                            {/* clients shouldn't be able to change 2FA enforcement settings, so the toggle
                            is visible only to platform users. See https://mews.atlassian.net/browse/OPS-19727 */}
                            {isPrivilegedPlatformUser && <ToggleMfa />}
                        </Stack>
                    )}
                </FloatingPanel>
            </OptimusProvider>

            <UnsavedChangesDialog
                active={confirmationActive}
                onCancel={toggleConfirmationDialog}
                onDiscardChanges={() => {
                    onConfirmationClose();
                    onClose();
                    reset();
                }}
            />
        </>
    );
};
